import * as React from "react";
import * as moment from "moment";
import ModernCard from "../core/ModernCard";
import Panel from "../core/Panel";
import Button from "../core/Button";
import { Link } from "react-router-dom";
import { jfetch, toast } from "../../utils";
import usePromise from "../hooks/usePromise";
import { getCommissionsMonthStatus } from "../../libs/commission_calculator";
import Loader from "../core/Loader";
import { useState } from "react";
import { RoleBadge } from "../core/RoleBadges";
import * as Sentry from "@sentry/browser";

function CommissionsMonthStatus({ orgId, salespeopleCount, orgConfig }) {
  const now = moment();

  const [status, { reload: reloadStatus, loading }] = usePromise(
    () => getCommissionsMonthStatus(orgId),
    [orgId]
  );

  let content;
  if (loading) {
    content = <Loader />;
  } else if (salespeopleCount < 1) {
    content = (
      <Panel heading="No Salespeople" type="warning">
        <p>
          You do not have any employees with the <RoleBadge>Sales</RoleBadge>{" "}
          role! Start by adding a salesperson. You may use the button above, or
          edit your employees and give them the <RoleBadge>Sales</RoleBadge>{" "}
          role.
        </p>
      </Panel>
    );
  } else {
    const { usersMissingCommissions, commissions } = status;
    content = (
      <>
        <CreateCommissionsStepPanel
          usersMissingCommissions={usersMissingCommissions}
        />
        <SubmitToSalespeopleStepPanel
          usersMissingCommissions={usersMissingCommissions}
          remainingCommissionsCount={usersMissingCommissions.length}
          commissions={commissions}
          reloadStatus={reloadStatus}
        />
        <SubmitToFinanceStepPanel
          commissions={commissions}
          orgConfig={orgConfig}
        />
      </>
    );
  }

  return <ModernCard title={`${now.format("MMMM YYYY")} Status`} ta="left" m="0px">{content}</ModernCard>;
}

export default CommissionsMonthStatus;

function StepPanelHeading({ step, heading }) {
  return (
    <>
      <span className="badge">{step}</span> {heading}
    </>
  );
}

function CreateCommissionsStepPanel({ usersMissingCommissions }) {
  const count = usersMissingCommissions.length;

  let content;

  if (count > 0) {
    content = (
      <>
        <p style={{ padding: "5px", borderBottom: "1px solid #e6e6e6" }}>
          You have {count} remaining{" "}
          {count === 1 ? "salesperson" : "salespeople"} without a commission for
          this month:
        </p>

        <table className="table">
          {usersMissingCommissions.map(sp => (
            <tr key={sp.id} style={{ borderBottom: "1px solid #e6e6e6" }}>
              <td style={{ padding: "5px" }}>
                <Link to={`/salespeople/${sp.id}`}>{sp.name}</Link>
              </td>
            </tr>
          ))}
        </table>
      </>
    );
  } else {
    content = <p>All of your salespeople have commissions for this month!</p>;
  }

  return (
    <Panel
      heading={<StepPanelHeading step={1} heading="Create Commissions" />}
      type={count > 0 ? "warning" : "success"}
    >
      {content}
    </Panel>
  );
}

function SubmitToSalespeopleStepPanel({
  usersMissingCommissions,
  remainingCommissionsCount,
  commissions,
  reloadStatus,
}) {
  const [submitting, setSubmitting] = useState(false);

  let panelColor: BootstrapContextColor = "default";
  if (remainingCommissionsCount < 1) {
    panelColor = "primary";
  }
  if (commissions.every(cm => cm.userApproval === "approved")) {
    panelColor = "success";
  }

  const unreadyCommissions = commissions.filter(
    cm => cm.userApproval === "not_ready_for_approval"
  );

  const readyCommissions = commissions.filter(
    cm => cm.userApproval !== "not_ready_for_approval"
  );

  const hasUsersMissingCommissions =
    usersMissingCommissions && usersMissingCommissions.length > 0;
  const hasUnreadyCommissions = unreadyCommissions.length > 0;
  const hasReadyCommissions = readyCommissions.length > 0;

  let topContent,
    buttonDisabled = false;
  if (hasUnreadyCommissions && !hasReadyCommissions) {
    topContent = (
      <p>
        When you&apos;re ready, you can submit the commissions to your
        salespeople for review. You&apos;ll see the results here as they
        respond.
      </p>
    );
  } else if (hasUnreadyCommissions && hasReadyCommissions) {
    topContent = (
      <>
        <p>
          It appears you have already submitted some of this month&apos;s
          commissions for approval, but there are some new ones that have not
          been submitted. You may click the button again to submit those.
        </p>
        <p>
          {unreadyCommissions.map(cm => {
            return (
              <li key={cm.id}>
                <Link to={`/salespeople/${cm.user.id}/${cm.id}`}>
                  {cm.user.name}
                </Link>{" "}
                <span className="label label-default">Unsubmitted</span>
              </li>
            );
          })}
        </p>
      </>
    );
  } else if (hasUsersMissingCommissions) {
    buttonDisabled = true;
    topContent = (
      <p>
        There are still users missing commissions. You can move on to the next
        step but its recommended that you complete all commissions for the
        month.
      </p>
    );
  } else {
    // all commissions ready for approval
    buttonDisabled = true;
    topContent = (
      <p>
        All of this month&apos;s commissions are already submitted for approval.
        View the approval statuses below and move on to the next step when
        you&apos;re ready.
      </p>
    );
  }

  let bottomContent;
  if (readyCommissions.length > 0) {
    bottomContent = (
      <>
        <hr />
        <h4 style={{ padding: "5px", borderBottom: "1px solid #e6e6e6" }}>
          Approval Status
        </h4>
        <table className="table">
          {readyCommissions.map(cm => {
            let label;
            switch (cm.userApproval) {
              case "ready_for_approval":
                label = (
                  <span className="label label-info">Ready for Approval</span>
                );
                break;
              case "approved":
                label = <span className="label label-success">Approved</span>;
                break;
              case "needs_review":
                label = (
                  <span className="label label-warning">Needs Review</span>
                );
                break;
            }

            return (
              <tr key={cm.id} style={{ borderBottom: "1px solid #e6e6e6" }}>
                <td style={{ padding: "5px" }}>
                  <Link to={`/salespeople/${cm.user.id}/${cm.id}`}>
                    {cm.user.name}
                  </Link>
                </td>
                <td>{label}</td>
              </tr>
            );
          })}
        </table>
      </>
    );
  }

  return (
    <Panel
      heading={<StepPanelHeading step={2} heading="Submit for Review" />}
      type={panelColor}
    >
      {topContent}
      <p>
        <Button
          disabled={buttonDisabled}
          onClick={() => {
            setSubmitting(true);
            jfetch
              .get(`/commission_calculator/months/submit_to_sales`)
              .then(() => {
                setSubmitting(false);
                reloadStatus();
              })
              .catch(e => {
                setSubmitting(false);
                toast.error("Error while submitting month.");
                Sentry.captureException(e);
              });
          }}
          loading={submitting}
        >
          Submit to Salespeople
        </Button>
      </p>
      {bottomContent}
    </Panel>
  );
}

function SubmitToFinanceStepPanel({ commissions, orgConfig }) {
  const [submitting, setSubmitting] = useState(false);

  let panelColor: BootstrapContextColor = "default";
  if (commissions.every(cm => cm.userApproval === "approved")) {
    panelColor = "primary";
  }

  const missingFinanceEmail = orgConfig && !orgConfig.financeEmail;

  return (
    <Panel
      type={panelColor}
      heading={<StepPanelHeading step={3} heading="Send to Finance" />}
    >
      <p>
        Finally, when everything is looking okay send it all over to your
        finance department in CSV format.
      </p>
      <Button
        disabled={missingFinanceEmail}
        loading={submitting}
        onClick={() => {
          setSubmitting(true);
          jfetch
            .get(`/commission_calculator/months/submit_to_finance`)
            .then(() => {
              setSubmitting(false);
            })
            .catch(e => {
              setSubmitting(false);
              toast.error("Error while submitting month.");
              Sentry.captureException(e);
            });
        }}
      >
        Send to Finance
      </Button>
      {missingFinanceEmail && (
        <p className="text-danger">
          You are missing an email for your finance department, please add it in
          the Settings section at the bottom of this page.
        </p>
      )}
    </Panel>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/CommissionsMonthStatus.tsx